import React, { useState, useRef, useEffect } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/* ===== Helpers d'URL au niveau module (pas de dépendance de hook) ===== */
const API_BASE = (api?.defaults?.baseURL || "").replace(/\/$/, "");
function resolveImgUrl(u) {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${API_BASE}${u}`;
  return `${API_BASE}/${u}`;
}

const CatalogueForm = ({ item, onClose }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(item?.name || "");
  const [volume, setVolume] = useState(item?.volume ?? "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(
    item?.image_url ? resolveImgUrl(item.image_url) : null
  );
  const fileInputRef = useRef();

  // Quand on ouvre un autre item à éditer → reset du state + preview
  useEffect(() => {
    setName(item?.name || "");
    setVolume(item?.volume ?? "");
    setFile(null);
    setPreview(item?.image_url ? resolveImgUrl(item.image_url) : null);
  }, [item]);

  // Génère l'aperçu quand un fichier est choisi
  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && droppedFile.type.startsWith("image/")) {
      setFile(droppedFile);
    }
  };

  // Upload de l'image si nécessaire (sinon on garde l'actuelle)
  const uploadImageIfNeeded = async () => {
    if (!file) {
      if (item?.image_url) {
        const currentName = item.image_url.split("/").pop();
        return { image_name: currentName };
      }
      return { image_name: undefined };
    }

    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/inventory/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { image_name: res.data.image_name }; // { image_name, image_url, relative_url }
    } catch {
      toast.error(t("catalogue.imageError"));
      throw new Error("Upload failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || volume === "" || volume == null) {
      return toast.error(t("catalogue.fillAll"));
    }

    try {
      const { image_name } = await uploadImageIfNeeded();
      const payload = {
        name,
        volume: Number(volume),
        ...(image_name ? { image_name } : {}),
      };

      if (item) {
        await api.put(`/inventory/${item.id}`, payload);
        toast.success(t("catalogue.updated"));
      } else {
        await api.post("/inventory", payload);
        toast.success(t("catalogue.created"));
      }
      onClose();
    } catch {
      toast.error(t("catalogue.saveError"));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-base-100 rounded-xl shadow-xl p-8 w-full max-w-lg relative border border-base-200">
        <h2 className="text-xl font-bold text-base-content mb-4">
          {item ? t("quote.editItem", "Edit Item") : t("quote.newItem", "New Item")}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t("quote.name", "Name")}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none text-base-content w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1">
              {t("quote.volume", "Volume (m³)")}
            </label>
            <input
              type="number"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(e.target.value)}
              className="input input-bordered bg-base-200 border-base-300 focus:border-primary focus:outline-none text-base-content w-full"
              required
            />
          </div>

          <div
            className="w-full h-40 border-2 border-dashed border-base-300 rounded-lg flex items-center justify-center text-base-content/50 hover:border-primary/50 transition cursor-pointer bg-base-200"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current.click()}
          >
            {preview ? (
              <img src={preview} alt="Preview" className="h-36 object-contain" />
            ) : (
              <span className="text-sm">{t("quote.dragDropOrClick", "Drag & drop image here or click to select")}</span>
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              {t("quote.cancel", "Cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {t("quote.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CatalogueForm;
